/**
 * VÉRAULT Portal — Notifications Bell (Pass C · S3)
 *
 * The operational command layer's top-bar entry. Pulls the unified inbox
 * (notifications + overdue payments + agreement deadlines + urgent planning)
 * and surfaces actions: open · dismiss · snooze · mark read.
 *
 * Calm by design — no badges shouting unless something is actually urgent.
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bell,
  Check,
  X,
  Clock,
  AlertCircle,
  ArrowRight,
  Loader2,
  CheckCheck,
} from "lucide-react";
import { api } from "../index";
import { toast } from "sonner";

const URGENCY_COLOR = {
  urgent: "text-[hsl(var(--accent-terracotta))]",
  warn: "text-[hsl(var(--accent-gold))]",
  unread: "text-[hsl(var(--foreground))]",
  normal: "text-[hsl(var(--foreground-muted))]",
};

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso);
    const diff = Math.max(0, Date.now() - then.getTime());
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return then.toLocaleDateString("en-US");
  } catch {
    return "";
  }
}

export default function NotificationsBell() {
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [busyIds, setBusyIds] = React.useState(new Set());
  const rootRef = React.useRef(null);
  const navigate = useNavigate();

  const refresh = React.useCallback(async () => {
    try {
      const { data } = await api.get("/travels/notifications/inbox", {
        params: { limit: 30 },
      });
      setData(data);
    } catch {
      setData({ items: [], unread_count: 0, urgent_count: 0, total: 0 });
    }
  }, []);

  React.useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000); // gentle 60s poll
    return () => clearInterval(t);
  }, [refresh]);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const withBusy = async (id, fn) => {
    setBusyIds((prev) => new Set(prev).add(id));
    try {
      await fn();
    } finally {
      setBusyIds((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  };

  const markRead = (item) =>
    withBusy(item.id, async () => {
      if (item.kind === "notification" && item.source_id) {
        try {
          await api.patch(`/travels/notifications/${item.source_id}/read`);
        } catch {}
      }
      await refresh();
    });

  const dismiss = (item) =>
    withBusy(item.id, async () => {
      if (item.kind === "notification" && item.source_id) {
        try {
          await api.patch(`/travels/notifications/${item.source_id}/dismiss`);
          toast.success("Dismissed.");
        } catch {
          toast.error("Couldn't dismiss.");
        }
      } else {
        toast.message("This item resolves itself once the underlying issue is addressed.");
      }
      await refresh();
    });

  const snooze = (item, hours = 24) =>
    withBusy(item.id, async () => {
      if (item.kind === "notification" && item.source_id) {
        try {
          await api.patch(`/travels/notifications/${item.source_id}/snooze`, null, {
            params: { hours },
          });
          toast.success(`Snoozed for ${hours}h.`);
        } catch {
          toast.error("Couldn't snooze.");
        }
      } else {
        toast.message("Snooze isn't available for this kind of alert yet.");
      }
      await refresh();
    });

  const openItem = async (item) => {
    if (item.kind === "notification" && item.source_id && !item.read) {
      try { await api.patch(`/travels/notifications/${item.source_id}/read`); } catch {}
    }
    if (item.deeplink) {
      setOpen(false);
      navigate(item.deeplink);
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/travels/notifications/mark-all-read");
      toast.success("Marked all read.");
      await refresh();
    } catch {
      toast.error("Couldn't mark all read.");
    }
  };

  const urgentCount = data?.urgent_count || 0;
  const unreadCount = data?.unread_count || 0;
  const showBadge = urgentCount + unreadCount > 0;
  const badgeTone = urgentCount > 0 ? "bg-[hsl(var(--accent-terracotta))]" : "bg-[hsl(var(--accent-gold))]";

  return (
    <div ref={rootRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="portal-notifications-trigger"
        className="relative inline-flex items-center justify-center size-9 rounded-md text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]"
        aria-label="Notifications"
        title="Notifications"
      >
        <Bell className="size-4" strokeWidth={1.5} />
        {showBadge && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 inline-flex items-center justify-center text-white font-mono text-[9px] tracking-wider rounded-full ${badgeTone}`}
            data-testid="portal-notifications-badge"
          >
            {urgentCount > 0 ? urgentCount : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            className="absolute right-0 mt-2 w-[420px] max-w-[92vw] bg-[hsl(var(--background))] rounded-xl border border-[rgba(0,0,0,0.06)] shadow-floating overflow-hidden z-50"
            data-testid="portal-notifications-panel"
          >
            <div className="px-5 py-4 border-b border-[rgba(0,0,0,0.05)] flex items-center justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                  Inbox
                </p>
                <h3 className="mt-1 font-serif text-base text-[hsl(var(--foreground))]">
                  {urgentCount > 0
                    ? `${urgentCount} urgent`
                    : unreadCount > 0
                    ? `${unreadCount} new`
                    : "All clear"}
                </h3>
              </div>
              <button
                onClick={markAllRead}
                disabled={unreadCount === 0}
                className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="portal-notifications-mark-all"
              >
                <CheckCheck className="size-3" strokeWidth={1.5} />
                Mark all read
              </button>
            </div>

            <div className="max-h-[420px] overflow-y-auto" data-testid="portal-notifications-list">
              {!data ? (
                <div className="p-6 text-center">
                  <Loader2 className="size-4 animate-spin inline" />
                </div>
              ) : data.items.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-secondary))]">
                    Nothing on the horizon. The atelier is quiet.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-[rgba(0,0,0,0.04)]">
                  {data.items.map((item, idx) => {
                    const busy = busyIds.has(item.id);
                    return (
                      <li
                        key={`${item.id || "n"}-${idx}`}
                        className={`group relative px-5 py-3.5 hover:bg-[hsl(var(--background-paper))]/50 transition-colors ${
                          !item.read ? "bg-[hsl(var(--background-paper))]/30" : ""
                        }`}
                        data-testid={`portal-notification-${item.id}`}
                      >
                        <button
                          onClick={() => openItem(item)}
                          className="block text-left w-full pr-16"
                          data-testid={`portal-notification-${item.id}-open`}
                        >
                          <div className="flex items-start gap-2.5">
                            {item.urgency === "urgent" && (
                              <AlertCircle
                                className="size-3.5 mt-0.5 text-[hsl(var(--accent-terracotta))] shrink-0"
                                strokeWidth={1.5}
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <p
                                className={`font-body text-[13.5px] ${URGENCY_COLOR[item.urgency] || URGENCY_COLOR.normal} ${
                                  !item.read ? "font-medium" : ""
                                }`}
                              >
                                {item.title}
                              </p>
                              {item.subtitle && (
                                <p className="mt-0.5 font-accent italic text-[12px] text-[hsl(var(--foreground-secondary))] truncate">
                                  {item.subtitle}
                                </p>
                              )}
                              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                                {timeAgo(item.created_at)} · {item.kind.replace(/_/g, " ")}
                              </p>
                            </div>
                          </div>
                        </button>
                        {/* Per-row actions */}
                        <div className="absolute top-3 right-4 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          {item.deeplink && (
                            <button
                              onClick={() => openItem(item)}
                              className="size-6 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] hover:bg-white"
                              title="Open"
                              data-testid={`portal-notification-${item.id}-go`}
                            >
                              <ArrowRight className="size-3" strokeWidth={1.5} />
                            </button>
                          )}
                          {!item.read && item.kind === "notification" && (
                            <button
                              onClick={() => markRead(item)}
                              disabled={busy}
                              className="size-6 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-sage,170_18%_42%))] hover:bg-white disabled:opacity-50"
                              title="Mark read"
                              data-testid={`portal-notification-${item.id}-read`}
                            >
                              <Check className="size-3" strokeWidth={1.5} />
                            </button>
                          )}
                          {item.kind === "notification" && (
                            <button
                              onClick={() => snooze(item, 24)}
                              disabled={busy}
                              className="size-6 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] hover:bg-white disabled:opacity-50"
                              title="Snooze 24h"
                              data-testid={`portal-notification-${item.id}-snooze`}
                            >
                              <Clock className="size-3" strokeWidth={1.5} />
                            </button>
                          )}
                          {item.kind === "notification" && (
                            <button
                              onClick={() => dismiss(item)}
                              disabled={busy}
                              className="size-6 inline-flex items-center justify-center rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))] hover:bg-white disabled:opacity-50"
                              title="Dismiss"
                              data-testid={`portal-notification-${item.id}-dismiss`}
                            >
                              <X className="size-3" strokeWidth={1.5} />
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="px-5 py-3 border-t border-[rgba(0,0,0,0.05)] bg-[hsl(var(--background-paper))]/40">
              <Link
                to="/portal/advisor/planning"
                onClick={() => setOpen(false)}
                className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))] hover:underline"
                data-testid="portal-notifications-see-planning"
              >
                See planning command center →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
